import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../../services/api';
import Topbar from '../../Topbar';
import { useTheme } from '../../ThemeContext';
import useResponsive from '../ResponsiveHook';

// ── Internal Components ───────────────────────────────────
function Card({ children, style = {} }) {
  const { C } = useTheme();
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 20, ...style }}>
      {children}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────
function SectionHeader({ title, sub }) {
  const { C } = useTheme();
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: C.textPrimary, letterSpacing: "-0.02em" }}>{title}</span>
      </div>
      <p style={{ fontSize: 12, color: C.textSecond, marginBottom: 18, fontWeight: 500, opacity: 0.8 }}>{sub}</p>
    </>
  );
}

// ── Profile Page ──────────────────────────────────────────
export default function Profile() {
  const { C } = useTheme();
  const { isMobile } = useResponsive(); 
  const context = useOutletContext();
  const onMenuClick = context ? context.onMenuClick : undefined;
  const [profile, setProfile]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);   

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData]   = useState({
    first_name: '',
    last_name: '',
    bio: '',
    education: '',
    candidate_address: '',
    cv: null
  });
  const [updating, setUpdating] = useState(false);
  
  // Petit déclencheur d'état pour permettre au bouton Retry de relancer le useEffect
  const [retryTrigger, setRetryTrigger] = useState(0);

  const syncEditData = (p) => {
    setEditData({
      first_name:        p.first_name        || '',
      last_name:         p.last_name         || '',
      bio:               p.bio               || '',
      education:         p.education         || '',
      candidate_address: p.candidate_address || '',
      cv: null
    });
  };

  // ✅ Solution 1 : fetchProfile est isolée proprement à l'intérieur du useEffect
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('candidates/profile/');
        if (res.data.status) {
          setProfile(res.data.data);
          // Version locale de la synchronisation pour éviter de dépendre d'une fonction externe
          setEditData({
            first_name:        res.data.data.first_name        || '',
            last_name:         res.data.data.last_name         || '',
            bio:               res.data.data.bio               || '',
            education:         res.data.data.education         || '',
            candidate_address: res.data.data.candidate_address || '',
            cv: null
          });
        }
      } catch (err) {
        console.error("Profile load error:", err);
        setError("Failed to load your profile. Please try again.");  
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [retryTrigger]); // Se relance uniquement si l'utilisateur clique sur Retry

  const handleCancel = () => {
    if (profile) syncEditData(profile);
    setIsEditing(false);
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append('first_name',         editData.first_name);
      formData.append('last_name',          editData.last_name);
      formData.append('bio',                editData.bio);
      formData.append('education',          editData.education);
      formData.append('candidate_address',  editData.candidate_address);
      if (editData.cv) formData.append('cv', editData.cv);

      const res = await api.put('candidates/profile/update/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.status) {
        setProfile(res.data.data);
        syncEditData(res.data.data);
        setIsEditing(false);
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update profile.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.bgDeep, color: C.textSecond, fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.accent, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.05em' }}>SYNCHRONIZING...</span>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: C.bgDeep, fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <p style={{ fontSize: 15, color: C.textSecond, fontWeight: 500 }}>{error}</p>
          <button
            onClick={() => setRetryTrigger(prev => prev + 1)} // ✅ Incrémente le trigger pour relancer le useEffect
            style={{ background: C.gradMain, color: "#fff", border: "none", padding: "10px 24px", borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: "pointer" }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const userData = profile || { first_name: '', last_name: '', bio: '', skills: [], wanted_jobs: [], education: '', candidate_address: '' };
  const initials = `${userData.first_name?.[0] || ''}${userData.last_name?.[0] || ''}`.toUpperCase();

  const inputStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: `1px solid ${C.border}`,
    color: C.textPrimary,
    padding: "12px 16px",
    borderRadius: 10,
    width: "100%",
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box"
  };

  const buttonStyle = {
    padding: "10px 20px", 
    borderRadius: 12, 
    fontSize: 13, 
    fontWeight: 700, 
    cursor: "pointer",
    textAlign: "center"
  };

  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column",
      background: C.bgDeep, overflowY: "auto",
      fontFamily: "'DM Sans', sans-serif", WebkitFontSmoothing: "antialiased"
    }}>
      <Topbar title="Profile" onMenuClick={onMenuClick} />

      <div style={{ 
        padding: isMobile ? "16px" : "32px", 
        display: "flex", 
        flexDirection: "column", 
        gap: 24, 
        maxWidth: 1200, 
        margin: "0 auto", 
        width: "100%", 
        boxSizing: "border-box" 
      }}>

        {/* ── Banner ────────────────────────────────────── */}
        <div style={{
          background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: isMobile ? "20px" : "32px",
          position: "relative", overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
          width: "100%", boxSizing: "border-box"
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: C.gradBar }} />

          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            flexWrap: "wrap-reverse", 
            gap: 24, 
            width: "100%",
            justifyContent: "center"
          }}>
            
            <div style={{
              width: 80, height: 80, borderRadius: 20, background: C.gradMain,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28, fontWeight: 800, fontFamily: "'Space Mono', monospace", color: "#fff",
              boxShadow: "0 12px 30px rgba(99,102,241,0.3)", border: "4px solid rgba(255,255,255,0.1)",
              flexShrink: 0
            }}>
              {initials || "?"}
            </div>

            <div style={{ flex: "1 1 280px", minWidth: 0, width: "100%" }}>
              <div style={{ 
                display: "flex", 
                flexWrap: "wrap", 
                justifyContent: "space-between", 
                alignItems: "center", 
                gap: 20,
                width: "100%"
              }}>
                
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  {isEditing ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
                      <input style={{ ...inputStyle, flex: "1 1 120px" }}
                        value={editData.first_name}
                        onChange={e => setEditData({ ...editData, first_name: e.target.value })}
                        placeholder="First Name" />
                      <input style={{ ...inputStyle, flex: "1 1 120px" }}
                        value={editData.last_name}
                        onChange={e => setEditData({ ...editData, last_name: e.target.value })}
                        placeholder="Last Name" />
                    </div>
                  ) : (
                    <h1 style={{ 
                      fontSize: isMobile ? 22 : 32, 
                      fontWeight: 800, 
                      color: C.textPrimary, 
                      marginBottom: 8, 
                      letterSpacing: "-0.04em",
                      whiteSpace: "nowrap",       
                      overflow: "hidden",        
                      textOverflow: "ellipsis",  
                      width: "100%",
                      display: "block"
                    }}>
                      {userData.first_name} {userData.last_name}
                    </h1>
                  )}

                  {isEditing ? (
                    <textarea style={{ ...inputStyle, height: 90, resize: "none", lineHeight: 1.6 }}
                      value={editData.bio}
                      onChange={e => setEditData({ ...editData, bio: e.target.value })}
                      placeholder="Write a brief professional bio..." />
                  ) : (
                    <p style={{ fontSize: 14, color: C.textSecond, lineHeight: 1.7, maxWidth: 700, fontWeight: 500, margin: 0, wordBreak: "break-word" }}>
                      {userData.bio || "No professional bio provided yet. Add one to stand out to employers."}
                    </p>
                  )}
                </div>

                <div style={{ 
                  display: "flex", 
                  gap: 12, 
                  width: isMobile ? "100%" : "auto",
                  flexShrink: 0,
                  justifyContent: "flex-end"
                }}>
                  {isEditing ? (
                    <>
                      <button onClick={handleCancel} style={{ ...buttonStyle, flex: isMobile ? 1 : "none", background: "rgba(255,255,255,0.05)", color: C.textPrimary, border: `1px solid ${C.border}` }}>
                        Cancel
                      </button>
                      <button onClick={handleUpdate} disabled={updating} style={{ ...buttonStyle, flex: isMobile ? 1 : "none", background: C.gradMain, color: "#fff", border: "none", opacity: updating ? 0.7 : 1, boxShadow: "0 4px 12px rgba(99,102,241,0.2)" }}>
                        {updating ? "Saving..." : "Save"}
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setIsEditing(true)} style={{ ...buttonStyle, width: isMobile ? "100%" : "auto", background: C.accentDim, color: C.accentBright, border: `1px solid rgba(99,102,241,0.3)` }}>
                      Modify Profile
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Info Cards ─────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>

          <Card>
            <SectionHeader title="Identity & Location" sub="Personal details and verification" />
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: C.textMuted, marginBottom: 8, fontWeight: 700 }}>Location</p>
                {isEditing ? (
                  <input style={inputStyle} value={editData.candidate_address}
                    onChange={e => setEditData({ ...editData, candidate_address: e.target.value })}
                    placeholder="e.g. Algiers, Algeria" />
                ) : (
                  <p style={{ fontSize: 14, color: C.textPrimary, fontWeight: 600, margin: 0 }}>{userData.candidate_address || "Location not set"}</p>
                )}
              </div>
              <div>
                <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: C.textMuted, marginBottom: 8, fontWeight: 700 }}>Academic Background</p>
                {isEditing ? (
                  <textarea style={{ ...inputStyle, height: 100, resize: "none", lineHeight: 1.6 }}
                    value={editData.education}
                    onChange={e => setEditData({ ...editData, education: e.target.value })}
                    placeholder="Mention your university, degree..." />
                ) : (
                  <p style={{ fontSize: 14, color: C.textSecond, lineHeight: 1.6, fontWeight: 500, margin: 0 }}>{userData.education || "No education history added."}</p>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <SectionHeader title="Professional Dossier" sub="Download and update your resume" />
            {isEditing ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: C.textMuted, fontWeight: 700 }}>Update CV (PDF Format)</p>
                <div style={{ border: `2px dashed ${C.border}`, borderRadius: 12, padding: 16, textAlign: 'center', background: 'rgba(255,255,255,0.01)', overflow: "hidden" }}>
                  <input type="file" accept=".pdf"
                    onChange={e => setEditData({ ...editData, cv: e.target.files[0] })}
                    style={{ color: C.textSecond, fontSize: 12, cursor: 'pointer', maxWidth: "100%" }} />
                </div>
              </div>
            ) : userData.cv ? (
              <div style={{ 
                display: "flex", 
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center", 
                gap: 16, 
                padding: "16px", 
                borderRadius: 12, 
                background: "rgba(16,185,129,0.03)", 
                border: "1px solid rgba(16,185,129,0.1)",
                textAlign: isMobile ? "center" : "left",
                width: "100%",
                boxSizing: "border-box"
              }}>
                <div style={{ fontSize: 32 }}>📄</div>
                <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
                  <p style={{ fontSize: 14, fontWeight: 700, color: C.green, marginBottom: 2 }}>Curriculum Vitae</p>
                  <p style={{ fontSize: 12, color: C.textSecond, fontWeight: 500, margin: 0 }}>Ready for applications</p>
                </div>
                <a href={userData.cv} target="_blank" rel="noreferrer"
                  style={{ 
                    fontSize: 13, color: "#fff", background: C.green, padding: "8px 18px", borderRadius: 10, 
                    textDecoration: "none", fontWeight: 700, boxShadow: "0 4px 12px rgba(16,185,129,0.2)",
                    width: isMobile ? "100%" : "auto", textAlign: "center", boxSizing: "border-box" 
                  }}>
                  Download
                </a>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '10px 0', color: C.textMuted }}>
                <p style={{ fontSize: 14, fontStyle: "italic", marginBottom: 10 }}>No CV uploaded yet.</p>
                <button onClick={() => setIsEditing(true)} style={{ color: C.accent, background: 'none', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
                  + Upload Resume
                </button>
              </div>
            )}
          </Card>

        </div>

        {/* ── Skills ─────────────────────────────────────── */}
        <Card>
          <SectionHeader title="Technical Stack" sub="Verified skills and expert competencies" />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {userData.skills?.length > 0
              ? userData.skills.map(s => (
                <span key={s.id} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: C.accentDim, border: `1px solid rgba(99,102,241,0.2)`, color: C.accentBright }}>{s.name}</span>
              ))
              : <p style={{ fontSize: 14, color: C.textMuted, fontStyle: "italic", margin: 0 }}>No skills verified yet.</p>
            }
          </div>
        </Card>

        {/* ── Goals ──────────────────────────────────────── */}
        <Card>
          <SectionHeader title="Strategic Goals" sub="Target roles and career focus" />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {userData.wanted_jobs?.length > 0
              ? userData.wanted_jobs.map(j => (
                <span key={j.id} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: "rgba(52,211,153,0.05)", border: `1px solid rgba(52,211,153,0.1)`, color: "#34d399" }}>{j.name}</span>
              ))
              : <p style={{ fontSize: 14, color: C.textMuted, fontStyle: "italic", margin: 0 }}>No career goals added.</p>
            }
          </div>
        </Card>

      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}